import { APIURL } from "../services/axiosServices/ApiEndPoints";
import { generateRequestSignature } from "./RequestSigning";

const REPORT_DEDUPE_WINDOW_MS = 30000;
const dedupeMap = new Map();
let isBound = false;

// Patterns that are noise, not real bugs — never report these
const IGNORED_MESSAGE_PATTERNS = [
  /^Script error\.?$/i,                     // cross-origin script, no useful info
  /ResizeObserver loop/i,                    // benign browser quirk
  /Non-Error promise rejection captured/i,   // usually third-party libs rejecting with non-Error
  /Network request failed/i,
  /Load failed/i,                            // Safari's version of chunk/network failures
  /extensions\/.*\.js/i,
];

const IGNORED_STACK_PATTERNS = [
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
  /^\s*$/,                                   // no stack at all — nothing actionable to fix
];

const isIgnorableError = (message, stack) => {
  if (IGNORED_MESSAGE_PATTERNS.some((re) => re.test(message))) return true;
  if (stack && IGNORED_STACK_PATTERNS.some((re) => re.test(stack))) return true;
  return false;
};

const isAbortError = (error) =>
  error?.name === "AbortError" || /aborted|cancelled|canceled/i.test(error?.message || "");

const decodeMinifiedReactError = (message) => {
  const match = message.match(/Minified React error #(\d+)/);
  if (!match) return message;
  return `${message} — see https://react.dev/errors/${match[1]}`;
};

const buildEndpoint = () => {
  const baseUrl = APIURL;
  return `${baseUrl.replace(/\/+$/, "")}/ClientError/Log`;
};

const applyRequestSigning = (requestUrl) => {
  try {
    const parsedUrl = new URL(requestUrl);
    const endpoint = parsedUrl.pathname + parsedUrl.search;
    const { timestamp, nonce, signature } = generateRequestSignature(endpoint);
    return {
      "X-Timestamp": timestamp,
      "X-Nonce": nonce,
      "X-Signature": signature,
    };
  } catch (error) {
    return {};
  }
};

const getSafeValue = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }

  if (typeof value === "string") {
    return value.slice(0, 4000);
  }

  return String(value).slice(0, 4000);
};

const reportClientException = (error, additionalData = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const errorMessage = error && error.message ? error.message : (error && error.toString ? error.toString() : "Unknown client-side error");
    const stackTrace = error && error.stack ? error.stack : "";

    // React error boundary catches are always fatal — a subtree unmounted
    // and the user is looking at broken/blank UI. Everything else (window
    // errors, unhandled rejections) gets filtered against known noise.
    const isBoundaryCatch = additionalData.source === "react-error-boundary";

    if (!isBoundaryCatch) {
      if (isIgnorableError(errorMessage, stackTrace)) return;
      if (isAbortError(error)) return;
    }

    const source = additionalData.source || (error && error.source ? error.source : "");
    const project = additionalData.project || "sgtech-admin";
    const payload = {
      message: getSafeValue(decodeMinifiedReactError(errorMessage)),
      stack: getSafeValue(stackTrace),
      source: getSafeValue(source),
      project: getSafeValue(project),
      cause: getSafeValue(errorMessage),
      url: getSafeValue(window.location.href),
      page: getSafeValue(window.location.pathname),
      userAgent: getSafeValue(navigator?.userAgent || ""),
      componentStack: getSafeValue(additionalData.componentStack || ""),
      extra: getSafeValue(JSON.stringify({
        ...additionalData.extra,
        project,
        route: window.location.pathname,
        hash: window.location.hash,
      })),
      timestamp: new Date().toISOString(),
    };

    const dedupeKey = `${payload.message}|${payload.stack}|${payload.url}|${payload.source}`;
    const now = Date.now();
    const lastSentAt = dedupeMap.get(dedupeKey);

    if (lastSentAt && now - lastSentAt < REPORT_DEDUPE_WINDOW_MS) {
      return;
    }

    dedupeMap.set(dedupeKey, now);
    const endpoint = buildEndpoint();
    const body = JSON.stringify(payload);
    const signingHeaders = applyRequestSigning(endpoint);

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...signingHeaders,
      },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (reportingError) {
    // Intentionally swallow reporting failures to avoid recursive loops.
  }
};

const bindClientErrorReporter = () => {
  if (typeof window === "undefined" || isBound) {
    return;
  }

  isBound = true;

  window.addEventListener("error", (event) => {
    const reason = event.error || { message: event.message };
    reportClientException(reason, {
      source: "window-error",
      project: "sgtech-admin",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason || {};
    reportClientException(
      { message: reason.message || String(reason), stack: reason.stack || "" },
      { source: "unhandledrejection", project: "sgtech-admin" }
    );
  });
};

export { bindClientErrorReporter, reportClientException };